import React, { useState, useEffect } from 'react';
import moment from 'moment';
import toast from 'react-hot-toast';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { AttendanceService } from '@/services/Attendance/AttendanceService';
import { ThrowError } from '@/api/callApi';
import type { IAttendanceDetail, IMarkAttendancePayload } from '@/interface/attendance.interface';
import type { shiftAndTime } from '@/interface/shiftAndTime.interface';
import type { AttendanceStatus } from '@/hooks/useAttendanceRules';
import { Calendar, AlertTriangle, Briefcase, MapPin, FileText, Clock, X, Loader2, ShieldCheck } from 'lucide-react';
import { showToastNotification } from '@/utility/helper';

export interface AttendanceDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    employeeName: string;
    empCode: string;
    department: string;
    fullDate: string;
    dayName: string;
    isSunday: boolean;
    status: AttendanceStatus;
    detail: IAttendanceDetail | null;
    shift?: shiftAndTime | null;
    userId?: string;
    onSuccess?: () => void;
}

const STATUS_LABEL_MAP: Record<AttendanceStatus, string> = {
    '--': 'No Data',
    'PRESENT': 'Present',
    'ABSENT': 'Absent',
    'WO': 'Week Off',
    'HOLIDAY': 'Holiday',
    'HALF_DAY': 'Half Day',
    'LEAVE': 'Leave',
    'WFH': 'Work From Home',
    'OUT_DUTY': 'Outdoor Duty',
    'F-D WFH': 'Full Day WFH',
    'F-D OD': 'Full Day Outdoor Duty',
    'WO+P': 'Week Off + Present',
    'HOLIDAY+P': 'Holiday + Present',
    'WFH+P': 'WFH + Present',
    'P+WFH': 'Present + WFH',
    'OD+P': 'Outdoor Duty + Present',
    'P+OD': 'Present + Outdoor Duty',
    'L+P': 'Leave + Present',
    'P+L': 'Present + Leave',
    'L+WFH': 'Leave + WFH',
    'WFH+L': 'WFH + Leave',
    'L+OD': 'Leave + Outdoor Duty',
    'OD+L': 'Outdoor Duty + Leave',
    'WFH+OD+P': 'WFH + OD + Present',
    'P+MISSED': 'Present (Missed Punch)',
    'WO+HD': 'Week Off + Half Day',
    'HOLIDAY+HD': 'Holiday + Half Day',
    'WFH+OD': 'WFH + Outdoor Duty',
};

const STATUS_BADGE_STYLE: Record<AttendanceStatus, string> = {
    '--': 'border-amber-200 text-amber-800 bg-amber-50 font-semibold',
    'PRESENT': 'border-emerald-200 text-emerald-800 bg-emerald-50 font-bold',
    'ABSENT': 'border-rose-200 text-rose-800 bg-rose-50 font-bold',
    'WO': 'border-pink-200 text-pink-800 bg-pink-50 font-semibold',
    'HOLIDAY': 'border-purple-200 text-purple-800 bg-purple-50 font-semibold',
    'HALF_DAY': 'border-sky-200 text-sky-800 bg-sky-50 font-bold',
    'LEAVE': 'border-violet-200 text-violet-800 bg-violet-50 font-semibold',
    'WFH': 'border-indigo-200 text-indigo-800 bg-indigo-50 font-semibold',
    'OUT_DUTY': 'border-amber-200 text-amber-800 bg-amber-50 font-semibold',
    'F-D WFH': 'border-blue-300 text-blue-900 bg-blue-100 font-extrabold',
    'F-D OD': 'border-orange-300 text-orange-900 bg-orange-100 font-extrabold',
    'WO+P': 'border-teal-300 text-teal-900 bg-teal-100 font-extrabold',
    'WO+HD': 'border-fuchsia-300 text-fuchsia-900 bg-fuchsia-100 font-extrabold',
    'HOLIDAY+P': 'border-lime-300 text-lime-900 bg-lime-100 font-extrabold',
    'HOLIDAY+HD': 'border-purple-300 text-purple-950 bg-purple-100 font-extrabold',
    'WFH+P': 'border-indigo-300 text-indigo-900 bg-indigo-100 font-extrabold',
    'P+WFH': 'border-indigo-300 text-indigo-900 bg-indigo-100 font-extrabold',
    'OD+P': 'border-amber-300 text-amber-950 bg-amber-100 font-extrabold',
    'P+OD': 'border-amber-300 text-amber-950 bg-amber-100 font-extrabold',
    'L+P': 'border-violet-300 text-violet-950 bg-violet-100 font-extrabold',
    'P+L': 'border-violet-300 text-violet-950 bg-violet-100 font-extrabold',
    'L+WFH': 'border-purple-300 text-purple-950 bg-purple-100 font-extrabold',
    'WFH+L': 'border-purple-300 text-purple-950 bg-purple-100 font-extrabold',
    'L+OD': 'border-orange-300 text-orange-950 bg-orange-100 font-extrabold',
    'OD+L': 'border-orange-300 text-orange-950 bg-orange-100 font-extrabold',
    'WFH+OD': 'border-cyan-300 text-cyan-950 bg-cyan-100 font-extrabold',
    'WFH+OD+P': 'border-cyan-400 text-cyan-950 bg-cyan-100 font-black',
    'P+MISSED': 'border-yellow-400 text-yellow-950 bg-amber-100 font-black',
};

const formatTime = (timeStr?: string | null): string => {
    if (!timeStr) return '--:--';
    if (timeStr.includes(':')) {
        const parts = timeStr.split(':');
        if (parts.length >= 2) {
            return moment(timeStr, ['HH:mm:ss', 'HH:mm', 'YYYY-MM-DDTHH:mm:ss']).format('hh:mm A');
        }
    }
    const m = moment(timeStr);
    return m.isValid() ? m.format('hh:mm A') : timeStr;
};

const getInitials = (name: string) => {
    if (!name) return 'E';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
};

const AttendanceDetailModal: React.FC<AttendanceDetailModalProps> = ({
    isOpen,
    onClose,
    employeeName,
    empCode,
    department,
    fullDate,
    dayName,
    isSunday,
    status,
    detail,
    shift,
    userId,
    onSuccess,
}) => {
    const [selectedMarkStatus, setSelectedMarkStatus] = useState<'PRESENT' | 'ABSENT' | 'HALF_DAY'>('PRESENT');
    const [selectedHalf, setSelectedHalf] = useState<'FIRST_HALF' | 'SECOND_HALF'>('FIRST_HALF');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    useEffect(() => {
        if (isOpen) {
            if (status.includes('ABSENT')) {
                setSelectedMarkStatus('ABSENT');
            } else if (status.includes('HALF_DAY')) {
                setSelectedMarkStatus('HALF_DAY');
            } else {
                setSelectedMarkStatus('PRESENT');
            }
            setSelectedHalf('FIRST_HALF');
        }
    }, [isOpen, status]);

    const formattedDate = moment(fullDate, 'YYYY-MM-DD').isValid()
        ? moment(fullDate, 'YYYY-MM-DD').format('dddd, DD MMMM YYYY')
        : `${dayName}, ${fullDate}`;

    const isHoliday = detail?.status === 'HOLIDAY' || status === 'HOLIDAY';

    const wfhReq = Array.isArray(detail?.wfhRequests) ? detail.wfhRequests[0] : detail?.wfhRequests;
    const odReq = Array.isArray(detail?.outdoorDuties) ? detail.outdoorDuties[0] : detail?.outdoorDuties;
    const leaveReq = Array.isArray(detail?.leaves) ? detail.leaves[0] : detail?.leaves;

    const rawLeaveDuration = String(leaveReq?.duration || (leaveReq as any)?.leaveDuration || '').toUpperCase();
    const currentStatusUpper = String(status || detail?.status || '').toUpperCase();

    // Check if First Half is Leave
    const isFirstHalfLeave =
        rawLeaveDuration.includes('FIRST') ||
        rawLeaveDuration.includes('1ST') ||
        rawLeaveDuration === 'FH' ||
        currentStatusUpper === 'L+P' ||
        currentStatusUpper === 'L+WFH' ||
        currentStatusUpper === 'L+OD' ||
        currentStatusUpper.startsWith('L+');

    // Check if Second Half is Leave
    const isSecondHalfLeave =
        rawLeaveDuration.includes('SECOND') ||
        rawLeaveDuration.includes('2ND') ||
        rawLeaveDuration === 'SH' ||
        currentStatusUpper === 'P+L' ||
        currentStatusUpper === 'WFH+L' ||
        currentStatusUpper === 'OD+L' ||
        currentStatusUpper.endsWith('+L');

    // Check if Full Day Leave (only when neither half is specifically on leave)
    const isFullDayLeave =
        !isFirstHalfLeave &&
        !isSecondHalfLeave &&
        (currentStatusUpper === 'LEAVE' ||
            rawLeaveDuration.includes('FULL') ||
            rawLeaveDuration === 'FD' ||
            (Boolean(leaveReq) && !rawLeaveDuration.includes('HALF')));

    // Exemptions:
    // First Half is exempt ONLY if First Half is on leave, Full day leave, Sunday, or Holiday
    const isFhExempt = isFullDayLeave || isFirstHalfLeave || isSunday || isHoliday;
    // Second Half is exempt ONLY if Second Half is on leave, Full day leave, Sunday, or Holiday
    const isShExempt = isFullDayLeave || isSecondHalfLeave || isSunday || isHoliday;

    const isFhInMissing = !isFhExempt && !detail?.firstHalfCheckInTime && !detail?.checkInTime;
    const isFhOutMissing = !isFhExempt && !detail?.firstHalfCheckOutTime;
    const isShInMissing = !isShExempt && !detail?.secondHalfCheckInTime;
    const isShOutMissing = !isShExempt && !detail?.secondHalfCheckOutTime && !detail?.checkOutTime;

    const missingPunchesList: string[] = [];
    if (isFhInMissing) missingPunchesList.push('1st Half Check-In');
    if (isFhOutMissing) missingPunchesList.push('1st Half Check-Out');
    if (isShInMissing) missingPunchesList.push('2nd Half Check-In');
    if (isShOutMissing) missingPunchesList.push('2nd Half Check-Out');

    const isMissedPunch = !isFullDayLeave && status === 'P+MISSED';
    const isAutoMarkedAbsent =
        !isFullDayLeave &&
        !isFirstHalfLeave &&
        !isSecondHalfLeave &&
        !isSunday &&
        !isHoliday &&
        (detail?.status === 'ABSENT' || status === 'ABSENT') &&
        Boolean(detail?.remarks?.toLowerCase().includes('absent'));

    const handleSubmitAttendance = async () => {
        const targetUserId = userId || detail?.userId;
        if (!targetUserId) {
            toast.error("Employee User ID is missing!");
            return;
        }

        setIsSubmitting(true);
        try {
            const fhInTime = shift?.shiftStartTime || "09:00:00";
            const fhOutTime = shift?.breakStartTime || "13:00:00";
            const shInTime = shift?.breakEndTime || "14:00:00";
            const shOutTime = shift?.shiftEndTime || "18:00:00";

            const payload: IMarkAttendancePayload = {
                userId: targetUserId,
                attendanceId: detail?.id || null,
                date: fullDate,
                status: selectedMarkStatus,
                markingAttendance: true,
                isMarkedByHRM: true,
            };

            if (selectedMarkStatus === 'PRESENT') {
                payload.firstHalfCheckInTime = fhInTime;
                payload.firstHalfCheckOutTime = fhOutTime;
                payload.secondHalfCheckInTime = shInTime;
                payload.secondHalfCheckOutTime = shOutTime;
            } else if (selectedMarkStatus === 'ABSENT') {
                payload.firstHalfCheckInTime = null;
                payload.firstHalfCheckOutTime = null;
                payload.secondHalfCheckInTime = null;
                payload.secondHalfCheckOutTime = null;
            } else if (selectedMarkStatus === 'HALF_DAY') {
                if (selectedHalf === 'FIRST_HALF') {
                    payload.firstHalfCheckInTime = fhInTime;
                    payload.firstHalfCheckOutTime = fhOutTime;
                    payload.secondHalfCheckInTime = null;
                    payload.secondHalfCheckOutTime = null;
                } else {
                    payload.firstHalfCheckInTime = null;
                    payload.firstHalfCheckOutTime = null;
                    payload.secondHalfCheckInTime = shInTime;
                    payload.secondHalfCheckOutTime = shOutTime;
                }
            }

            const response = await AttendanceService.markAttendance(payload);
            if (response?.status) {
                showToastNotification("success", response.message || "Attendance marked successfully!");
                if (onSuccess) onSuccess();
                onClose();
            } else {
                showToastNotification("danger", response.message || "Failed to mark attendance.");
            }
        } catch (error) {
            console.error("Error marking attendance:", error);
            ThrowError(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent showCloseButton={false} className="sm:max-w-xl p-0 overflow-hidden rounded-2xl border border-slate-200 shadow-xl bg-white">
                <DialogTitle className="sr-only">Attendance Details for {employeeName}</DialogTitle>

                <div className="flex flex-col relative bg-white w-full">
                    {/* Header Bar */}
                    <div className="bg-slate-900 text-white p-5 space-y-3 relative">
                        <button
                            onClick={onClose}
                            disabled={isSubmitting}
                            className="absolute top-4 right-4 z-10 p-1.5 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors cursor-pointer border border-slate-700"
                            aria-label="Close"
                        >
                            <X className="w-4 h-4" />
                        </button>

                        <div className="flex items-center justify-between pr-10">
                            <div className="flex items-center space-x-2 text-xs font-semibold text-slate-300">
                                <Calendar className="w-4 h-4 text-indigo-400" />
                                <span>{formattedDate}</span>
                            </div>

                            <span className={`px-3 py-1 text-xs rounded-full border ${STATUS_BADGE_STYLE[status] || STATUS_BADGE_STYLE['--']}`}>
                                {STATUS_LABEL_MAP[status] || status}
                            </span>
                        </div>

                        {/* Employee Card Info Header */}
                        <div className="flex items-center justify-between pt-2 border-t border-slate-800">
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-sm shadow-sm">
                                    {getInitials(employeeName)}
                                </div>
                                <div>
                                    <h3 className="text-base font-bold text-white leading-tight">{employeeName}</h3>
                                    <div className="flex items-center space-x-2 text-xs text-slate-400 mt-0.5">
                                        <span className="font-mono font-semibold px-1.5 py-0.5 rounded bg-slate-800 text-indigo-300 text-[11px] border border-slate-700">{empCode}</span>
                                        <span>•</span>
                                        <span>{department}</span>
                                    </div>
                                </div>
                            </div>

                            {shift && (
                                <div className="text-right hidden sm:block">
                                    <p className="text-[10px] uppercase font-bold text-slate-400">Shift</p>
                                    <p className="text-xs font-semibold text-indigo-300">{shift.shiftName || 'Standard Shift'}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Scrollable Content Body */}
                    <div className="p-5 space-y-4 bg-slate-50/50 max-h-[70vh] overflow-y-auto">
                        {/* Full Day Leave Banner */}
                        {isFullDayLeave && (
                            <div className="p-3.5 rounded-xl border flex items-center justify-between text-xs bg-violet-50 border-violet-200 text-violet-900">
                                <div className="flex items-center space-x-2.5">
                                    <Calendar className="w-4 h-4 text-violet-600 shrink-0" />
                                    <div>
                                        <p className="font-bold text-violet-950">
                                            On Leave {leaveReq?.leaveType ? `(${leaveReq.leaveType})` : ''}
                                        </p>
                                        <p className="text-[11px] text-violet-700">
                                            {leaveReq?.reason || leaveReq?.remarks || detail?.remarks || "Employee is on approved leave for this date."}
                                        </p>
                                    </div>
                                </div>
                                <span className="text-[10px] font-bold px-2.5 py-1 rounded-md border bg-violet-100 text-violet-800 border-violet-300">
                                    LEAVE
                                </span>
                            </div>
                        )}

                        {/* 1st Half Leave Banner */}
                        {isFirstHalfLeave && (
                            <div className="p-3.5 rounded-xl border flex items-center justify-between text-xs bg-violet-50 border-violet-200 text-violet-900">
                                <div className="flex items-center space-x-2.5">
                                    <Calendar className="w-4 h-4 text-violet-600 shrink-0" />
                                    <div>
                                        <p className="font-bold text-violet-950">
                                            1st Half Leave {leaveReq?.leaveType ? `(${leaveReq.leaveType})` : ''}
                                        </p>
                                        <p className="text-[11px] text-violet-700">
                                            {leaveReq?.reason || leaveReq?.remarks || detail?.remarks || "Employee is on approved First Half Leave."}
                                        </p>
                                    </div>
                                </div>
                                <span className="text-[10px] font-bold px-2.5 py-1 rounded-md border bg-violet-100 text-violet-800 border-violet-300">
                                    1ST HALF LEAVE
                                </span>
                            </div>
                        )}

                        {/* 2nd Half Leave Banner */}
                        {isSecondHalfLeave && (
                            <div className="p-3.5 rounded-xl border flex items-center justify-between text-xs bg-violet-50 border-violet-200 text-violet-900">
                                <div className="flex items-center space-x-2.5">
                                    <Calendar className="w-4 h-4 text-violet-600 shrink-0" />
                                    <div>
                                        <p className="font-bold text-violet-950">
                                            2nd Half Leave {leaveReq?.leaveType ? `(${leaveReq.leaveType})` : ''}
                                        </p>
                                        <p className="text-[11px] text-violet-700">
                                            {leaveReq?.reason || leaveReq?.remarks || detail?.remarks || "Employee is on approved Second Half Leave."}
                                        </p>
                                    </div>
                                </div>
                                <span className="text-[10px] font-bold px-2.5 py-1 rounded-md border bg-violet-100 text-violet-800 border-violet-300">
                                    2ND HALF LEAVE
                                </span>
                            </div>
                        )}

                        {/* Auto Marked Absent Banner */}
                        {!isFullDayLeave && (isAutoMarkedAbsent || (detail?.status === 'ABSENT' && missingPunchesList.length > 0 && !isSunday && !isHoliday)) && (
                            <div className="flex items-start space-x-3 p-3.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-900 text-xs">
                                <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                                <div className="space-y-1 w-full">
                                    <div className="flex items-center justify-between">
                                        <p className="font-bold text-rose-950">
                                            {detail?.remarks || "Auto Marked Absent"}
                                        </p>
                                    </div>
                                    <p className="text-rose-800 font-medium">
                                        Punch logs are incomplete or missing required check-in/out timestamps. System marked status as ABSENT.
                                    </p>
                                    {missingPunchesList.length > 0 && (
                                        <div className="pt-1.5 border-t border-rose-200 text-[11px] flex items-center space-x-1.5 flex-wrap gap-1">
                                            <span className="font-bold text-rose-950">Missing Logs:</span>
                                            {missingPunchesList.map((m, idx) => (
                                                <span key={idx} className="px-2 py-0.5 rounded bg-rose-100 border border-rose-300 text-rose-900 font-semibold text-[10px]">
                                                    {m}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Missed Punch Warning */}
                        {!isFullDayLeave && (missingPunchesList.length > 0 || isMissedPunch) && !isSunday && !isHoliday && (
                            <div className="flex items-start space-x-3 p-3.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-xs">
                                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                                <div className="space-y-1 w-full">
                                    <p className="font-bold text-amber-950">
                                        {isFirstHalfLeave
                                            ? "Incomplete / Missed Punch Warning (2nd Half)"
                                            : isSecondHalfLeave
                                                ? "Incomplete / Missed Punch Warning (1st Half)"
                                                : "Incomplete / Missed Punch Warning (P+MISSED)"}
                                    </p>
                                    <p className="text-amber-800">
                                        {isFirstHalfLeave
                                            ? "Employee is on 1st Half Leave, but required punch timestamps for 2nd Half are missing."
                                            : isSecondHalfLeave
                                                ? "Employee is on 2nd Half Leave, but required punch timestamps for 1st Half are missing."
                                                : "Check-in log exists for this date, but one or more required punch timestamps (check-out) are missing."}
                                    </p>
                                    {missingPunchesList.length > 0 && (
                                        <div className="pt-1 border-t border-amber-200 text-[11px] flex items-center space-x-1.5 flex-wrap gap-1">
                                            <span className="font-bold text-amber-950">Missing Punches:</span>
                                            {missingPunchesList.map((m, idx) => (
                                                <span key={idx} className="px-2 py-0.5 rounded bg-amber-100 border border-amber-300 text-amber-900 font-semibold text-[10px]">
                                                    {m}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {(isSunday || isHoliday) && (
                            <div className={`p-3.5 rounded-xl border flex items-center justify-between text-xs ${isSunday ? 'bg-rose-50 border-rose-200 text-rose-900' : 'bg-purple-50 border-purple-200 text-purple-900'}`}>
                                <div className="flex items-center space-x-2.5">
                                    <Calendar className={`w-4 h-4 ${isSunday ? 'text-rose-600' : 'text-purple-600'}`} />
                                    <div>
                                        <p className="font-bold">{isSunday ? 'Sunday (Weekly Off)' : 'Official Holiday'}</p>
                                        <p className="text-[11px] text-slate-600">
                                            {status.includes('+P') ? 'Employee worked on off day' : 'Non-Working Day'}
                                        </p>
                                    </div>
                                </div>
                                <span className={`text-[10px] font-bold px-2.5 py-1 rounded-md border ${isSunday ? 'bg-rose-100 text-rose-800 border-rose-300' : 'bg-purple-100 text-purple-800 border-purple-300'}`}>
                                    {isSunday ? 'WEEK OFF' : 'HOLIDAY'}
                                </span>
                            </div>
                        )}

                        {/* First Half & Second Half Punch Logs Cards */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {/* First Half */}
                            <div className="bg-white rounded-xl border border-slate-200 p-3.5 space-y-2.5 shadow-2xs">
                                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                                    <div className="flex items-center space-x-2">
                                        <div className="w-2.5 h-2.5 rounded-full bg-indigo-500" />
                                        <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wide">First Half</h4>
                                    </div>
                                    {wfhReq?.duration === 'FIRST_HALF' && (
                                        <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-indigo-50 text-indigo-700 border border-indigo-200">
                                            WFH
                                        </span>
                                    )}
                                    {odReq?.duration === 'FIRST_HALF' && (
                                        <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-orange-50 text-orange-700 border border-orange-200">
                                            OD
                                        </span>
                                    )}
                                    {(isFirstHalfLeave || isFullDayLeave) && (
                                        <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-violet-50 text-violet-700 border border-violet-200">
                                            LEAVE
                                        </span>
                                    )}
                                </div>

                                <div className="grid grid-cols-2 gap-2 text-center pt-0.5">
                                    <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-100 space-y-1">
                                        <p className="text-[10px] text-slate-400 font-bold uppercase flex items-center justify-center space-x-1">
                                            <Clock className="w-3 h-3 text-slate-400" />
                                            <span>In</span>
                                        </p>
                                        <p className="text-xs font-bold text-slate-800">
                                            {formatTime(detail?.firstHalfCheckInTime || detail?.checkInTime)}
                                        </p>
                                        {isFhInMissing && (
                                            <span className="inline-block text-[9px] font-semibold text-rose-600 bg-rose-50 px-1 py-0.2 rounded border border-rose-200">
                                                Missing
                                            </span>
                                        )}
                                    </div>

                                    <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-100 space-y-1">
                                        <p className="text-[10px] text-slate-400 font-bold uppercase flex items-center justify-center space-x-1">
                                            <Clock className="w-3 h-3 text-slate-400" />
                                            <span>Out</span>
                                        </p>
                                        <p className="text-xs font-bold text-slate-800">
                                            {formatTime(detail?.firstHalfCheckOutTime)}
                                        </p>
                                        {isFhOutMissing && (
                                            <span className="inline-block text-[9px] font-semibold text-rose-600 bg-rose-50 px-1 py-0.2 rounded border border-rose-200">
                                                Missing
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Second Half */}
                            <div className="bg-white rounded-xl border border-slate-200 p-3.5 space-y-2.5 shadow-2xs">
                                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                                    <div className="flex items-center space-x-2">
                                        <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                                        <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wide">Second Half</h4>
                                    </div>
                                    {wfhReq?.duration === 'SECOND_HALF' && (
                                        <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-indigo-50 text-indigo-700 border border-indigo-200">
                                            WFH
                                        </span>
                                    )}
                                    {odReq?.duration === 'SECOND_HALF' && (
                                        <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-orange-50 text-orange-700 border border-orange-200">
                                            OD
                                        </span>
                                    )}
                                    {(isSecondHalfLeave || isFullDayLeave) && (
                                        <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-violet-50 text-violet-700 border border-violet-200">
                                            LEAVE
                                        </span>
                                    )}
                                </div>

                                <div className="grid grid-cols-2 gap-2 text-center pt-0.5">
                                    <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-100 space-y-1">
                                        <p className="text-[10px] text-slate-400 font-bold uppercase flex items-center justify-center space-x-1">
                                            <Clock className="w-3 h-3 text-slate-400" />
                                            <span>In</span>
                                        </p>
                                        <p className="text-xs font-bold text-slate-800">
                                            {formatTime(detail?.secondHalfCheckInTime)}
                                        </p>
                                        {isShInMissing && (
                                            <span className="inline-block text-[9px] font-semibold text-rose-600 bg-rose-50 px-1 py-0.2 rounded border border-rose-200">
                                                Missing
                                            </span>
                                        )}
                                    </div>

                                    <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-100 space-y-1">
                                        <p className="text-[10px] text-slate-400 font-bold uppercase flex items-center justify-center space-x-1">
                                            <Clock className="w-3 h-3 text-slate-400" />
                                            <span>Out</span>
                                        </p>
                                        <p className="text-xs font-bold text-slate-800">
                                            {formatTime(detail?.secondHalfCheckOutTime || detail?.checkOutTime)}
                                        </p>
                                        {isShOutMissing && (
                                            <span className="inline-block text-[9px] font-semibold text-rose-600 bg-rose-50 px-1 py-0.2 rounded border border-rose-200">
                                                Missing
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Requests & Notes */}
                        {(wfhReq || odReq || leaveReq || detail?.remarks) && (
                            <div className="bg-white rounded-xl border border-slate-200 p-3.5 space-y-2 text-xs">
                                <h4 className="font-bold text-slate-800 uppercase tracking-wide flex items-center space-x-1.5">
                                    <FileText className="w-3.5 h-3.5 text-slate-500" />
                                    <span>Requests & Remarks</span>
                                </h4>

                                <div className="space-y-2">
                                    {wfhReq && (
                                        <div className="p-2.5 rounded-lg bg-indigo-50/50 border border-indigo-100 flex items-start space-x-2.5">
                                            <Briefcase className="w-3.5 h-3.5 text-indigo-600 shrink-0 mt-0.5" />
                                            <div>
                                                <p className="font-bold text-indigo-950">Work From Home ({wfhReq.duration || 'FULL_DAY'})</p>
                                                {wfhReq.reason && <p className="text-indigo-800 mt-0.5">Reason: {wfhReq.reason}</p>}
                                            </div>
                                        </div>
                                    )}

                                    {odReq && (
                                        <div className="p-2.5 rounded-lg bg-orange-50/50 border border-orange-100 flex items-start space-x-2.5">
                                            <MapPin className="w-3.5 h-3.5 text-orange-600 shrink-0 mt-0.5" />
                                            <div>
                                                <p className="font-bold text-orange-950">Outdoor Duty ({odReq.duration || 'FULL_DAY'})</p>
                                                {odReq.location && <p className="text-orange-800 mt-0.5">Location: {odReq.location}</p>}
                                                {odReq.reason && <p className="text-orange-800 mt-0.5">Reason: {odReq.reason}</p>}
                                            </div>
                                        </div>
                                    )}

                                    {leaveReq && (
                                        <div className="p-2.5 rounded-lg bg-violet-50/50 border border-violet-100 flex items-start space-x-2.5">
                                            <Calendar className="w-3.5 h-3.5 text-violet-600 shrink-0 mt-0.5" />
                                            <div>
                                                <p className="font-bold text-violet-950">
                                                    Leave ({leaveReq.duration || 'FULL_DAY'}) {leaveReq.leaveType ? `• ${leaveReq.leaveType}` : ''}
                                                </p>
                                                {leaveReq.reason && <p className="text-violet-800 mt-0.5">Reason: {leaveReq.reason}</p>}
                                                {leaveReq.remarks && <p className="text-violet-800 mt-0.5">Remarks: {leaveReq.remarks}</p>}
                                            </div>
                                        </div>
                                    )}

                                    {detail?.remarks && !leaveReq?.remarks && (
                                        <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-200 text-slate-700">
                                            <span className="font-bold">Remarks:</span> {detail.remarks}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* HRM Mark Attendance Panel */}
                        <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3 shadow-2xs">
                            <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                                <div className="flex items-center space-x-1.5">
                                    <ShieldCheck className="w-4 h-4 text-indigo-600" />
                                    <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wide">
                                        Mark / Update Attendance
                                    </h4>
                                </div>
                                <span className="text-[10px] font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                                    HR Action
                                </span>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[11px] font-semibold text-slate-600 block">Select Status:</label>
                                <div className="grid grid-cols-3 gap-2">
                                    <button
                                        type="button"
                                        disabled={isSubmitting}
                                        onClick={() => setSelectedMarkStatus('PRESENT')}
                                        className={`py-2 px-3 text-xs font-bold rounded-lg border transition-all cursor-pointer ${selectedMarkStatus === 'PRESENT'
                                            ? 'bg-emerald-600 text-white border-emerald-700 shadow-xs'
                                            : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                                            }`}
                                    >
                                        Present
                                    </button>
                                    <button
                                        type="button"
                                        disabled={isSubmitting}
                                        onClick={() => setSelectedMarkStatus('ABSENT')}
                                        className={`py-2 px-3 text-xs font-bold rounded-lg border transition-all cursor-pointer ${selectedMarkStatus === 'ABSENT'
                                            ? 'bg-rose-600 text-white border-rose-700 shadow-xs'
                                            : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                                            }`}
                                    >
                                        Absent
                                    </button>
                                    <button
                                        type="button"
                                        disabled={isSubmitting}
                                        onClick={() => setSelectedMarkStatus('HALF_DAY')}
                                        className={`py-2 px-3 text-xs font-bold rounded-lg border transition-all cursor-pointer ${selectedMarkStatus === 'HALF_DAY'
                                            ? 'bg-sky-600 text-white border-sky-700 shadow-xs'
                                            : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                                            }`}
                                    >
                                        Half Day
                                    </button>
                                </div>
                            </div>

                            {selectedMarkStatus === 'HALF_DAY' && (
                                <div className="p-3 rounded-lg bg-sky-50/60 border border-sky-200 space-y-1.5">
                                    <label className="text-[11px] font-semibold text-sky-950 block">Select Half:</label>
                                    <div className="grid grid-cols-2 gap-2">
                                        <button
                                            type="button"
                                            disabled={isSubmitting}
                                            onClick={() => setSelectedHalf('FIRST_HALF')}
                                            className={`py-1.5 px-3 text-xs font-bold rounded border transition-all cursor-pointer ${selectedHalf === 'FIRST_HALF'
                                                ? 'bg-sky-600 text-white border-sky-700 shadow-xs'
                                                : 'bg-white text-sky-900 border-sky-200 hover:bg-sky-100'
                                                }`}
                                        >
                                            First Half
                                        </button>
                                        <button
                                            type="button"
                                            disabled={isSubmitting}
                                            onClick={() => setSelectedHalf('SECOND_HALF')}
                                            className={`py-1.5 px-3 text-xs font-bold rounded border transition-all cursor-pointer ${selectedHalf === 'SECOND_HALF'
                                                ? 'bg-sky-600 text-white border-sky-700 shadow-xs'
                                                : 'bg-white text-sky-900 border-sky-200 hover:bg-sky-100'
                                                }`}
                                        >
                                            Second Half
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Footer Action Bar */}
                    <div className="p-4 border-t border-slate-200 bg-white flex items-center justify-end space-x-3">
                        <button
                            onClick={onClose}
                            disabled={isSubmitting}
                            className="px-4 py-2 text-xs font-semibold rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSubmitAttendance}
                            disabled={isSubmitting}
                            className="px-5 py-2 text-xs font-bold rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white transition-all cursor-pointer shadow-sm flex items-center space-x-2 disabled:opacity-50"
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="w-3.5 h-3.5 animate-spin text-white" />
                                    <span>Saving...</span>
                                </>
                            ) : (
                                <span>Save Attendance</span>
                            )}
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default AttendanceDetailModal;

